// apps/web/services/cartService.ts
import { productService } from '@cabukaka/services';
import { CartItem } from '@cabukaka/types';

const STORAGE_KEY = 'cart_items';

export interface StoredCartItem {
  productId: string;
  quantity: number;
  variantId?: string;
  variantName?: string;
  variantValue?: string;
  variantPrice?: number;
  variantImage?: string;
}

export const cartService = {
  getStoredItems: (): StoredCartItem[] => {
    if (typeof window === 'undefined') return [];
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return [];
    try {
      return JSON.parse(saved);
    } catch {
      return [];
    }
  },

  saveStoredItems: (items: StoredCartItem[]): void => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  },

  getCart: async (): Promise<{ items: CartItem[]; subtotal: number; deliveryFee: number; total: number }> => {
    const storedItems = cartService.getStoredItems();
    const deliveryFee = 2500;

    if (storedItems.length === 0) {
      return { items: [], subtotal: 0, deliveryFee, total: 0 };
    }

    // Récupérer les IDs des produits simples (sans variante)
    const productIds = storedItems.filter(item => !item.variantId).map(item => item.productId);
    const variantIds = storedItems.filter(item => item.variantId).map(item => item.productId);

    // Récupérer les données des produits simples
    let products: any[] = [];
    if (productIds.length > 0) {
      const productsData = await productService.getProductsByIds(productIds);
      products = productsData.data;
    }

    // Récupérer les données des produits pour les variantes (pour avoir shopId et shopName)
    let variantProducts: any[] = [];
    if (variantIds.length > 0) {
      const variantProductsData = await productService.getProductsByIds(variantIds);
      variantProducts = variantProductsData.data;
    }

    const items: CartItem[] = storedItems.map(stored => {
      // Cas d'une variante
      if (stored.variantId) {
        const variantProduct = variantProducts.find(p => p.id === stored.productId);
        return {
          id: stored.variantId,
          variantId: stored.variantId,
          productId: stored.productId,
          name: `${stored.variantName} - ${stored.variantValue}`,
          slug: '',
          price: stored.variantPrice || 0,
          priceLabel: `${(stored.variantPrice || 0).toLocaleString('fr-FR')} FCFA`,
          quantity: stored.quantity,
          image: stored.variantImage || '',
          inStock: true,
          shopId: variantProduct?.shopId || '',
          shopName: variantProduct?.shop?.name || '',
          shopLogo: variantProduct?.shop?.logo || ''
        };
      }

      // Cas d'un produit simple
      const product = products.find(p => p.id === stored.productId);
      return {
        id: stored.productId,
        productId: stored.productId,
        name: product?.name || '',
        slug: product?.slug || '',
        price: product?.price || 0,
        priceLabel: product?.priceLabel || '',
        quantity: stored.quantity,
        image: product?.mainImage || '',
        inStock: product?.inStock || false,
        shopId: product?.shopId || '',
        shopName: product?.shop?.name || '',
        shopLogo: product?.shop?.logo || ''
      };
    }).filter(item => item.name !== '');

    const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

    return { items, subtotal, deliveryFee, total: subtotal + deliveryFee };
  },

  addToCart: async (productId: string, quantity: number = 1, variant?: any): Promise<void> => {
    if (!productId) {
      console.error('addToCart called without productId');
      return;
    }

    const storedItems = cartService.getStoredItems();
    const existingIndex = storedItems.findIndex(item => {
      if (variant) {
        return item.variantId === variant.id;
      }
      return item.productId === productId && !item.variantId;
    });

    if (existingIndex !== -1) {
      storedItems[existingIndex].quantity += quantity;
    } else {
      if (variant) {
        storedItems.push({
          productId,
          quantity,
          variantId: variant.id,
          variantName: variant.name,
          variantValue: variant.value,
          variantPrice: variant.price,
          variantImage: variant.mainImage
        });
      } else {
        storedItems.push({ productId, quantity });
      }
    }

    cartService.saveStoredItems(storedItems);
  },

  updateQuantity: async (productId: string, quantity: number, variantId?: string): Promise<void> => {
    if (quantity < 1) return;

    const storedItems = cartService.getStoredItems();
    const index = storedItems.findIndex(item => {
      if (variantId) {
        return item.variantId === variantId;
      }
      return item.productId === productId && !item.variantId;
    });

    if (index !== -1) {
      storedItems[index].quantity = quantity;
      cartService.saveStoredItems(storedItems);
    }
  },

  removeFromCart: async (productId: string, variantId?: string): Promise<void> => {
    const storedItems = cartService.getStoredItems();
    const filtered = storedItems.filter(item => {
      if (variantId) {
        return item.variantId !== variantId;
      }
      return !(item.productId === productId && !item.variantId);
    });
    cartService.saveStoredItems(filtered);
  },

  clearCart: async (): Promise<void> => {
    cartService.saveStoredItems([]);
  },

  getItemCount: async (): Promise<number> => {
    const storedItems = cartService.getStoredItems();
    return storedItems.reduce((sum, item) => sum + item.quantity, 0);
  }
};